import React, {useState} from 'react';
import {Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography} from "@mui/material";
import {AdminPanelSettings} from "@mui/icons-material";
import {DateFormats, subscriptionTypeOptions} from "../constants/securspace-constants";
import moment from "moment";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {updateAccountAdmin} from "../user/request/user-requests";
import Busy from "../Busy";
import {withSnackbar} from "../hocs/withSnackbar";
import {formatCurrencyValue, isInteger, parseCurrencyValue, validateCurrencyValue} from "../../util/PaymentUtils";
import {requestLoggedInAuthoritySource} from "../../services/session/session-requests";
import {isSupplierAccount} from "../../util/AccountUtils";
import {styled} from "@mui/material/styles";

const AdminInfoColumnGap5 = styled(Grid)(() => ({
  columnGap: '0.36rem',
}));

const AdminInfoMarginTop20 = styled('form')(() => ({
  marginTop: '1.43rem',
}));

const AdminInfoMarginButton = styled(Button)(() => ({
  marginTop: '1.43rem',
}));

const AdminInfoDatePicker = styled(DatePicker)(() => ({
  width: '100%',
}));

const AdminInfoMarginTop5 = styled(Grid)(() => ({
  marginTop: '0.36rem',
}));

const AdminInfoRowGap10 = styled(Grid)(() => ({
  rowGap: '0.71rem',
}));

const AdminInfoInputLabel = styled(InputLabel)(() => ({
  fontSize: '0.87rem',
}));

const AdminInfo = (props) => {
  const {
    account,
    snackbarShowMessage,
    handleAccountUpdated
  } = props;

  const [feePercentage, setFeePercentage] = useState(account.feePercentage);
  const [subscriptionType, setSubscriptionType] = useState(account.subscriptionType);
  const [startDate, setStartDate] = useState(account.subscriptionEffectiveDate);
  const [subscriptionFee, setSubscriptionFee] = useState(account.subscriptionFee);
  const [billingFeePercentage, setBillingFeePercentage] = useState(account.billingFeePercentage);
  const [securspaceServiceFeePercentage, setSecurspaceServiceFeePercentage] = useState(account.securspaceServiceFeePercentage);
  const [changesMade, setChangesMade] = useState(false);

  const valueInRange = (value, min, max) => {
    return value >= min && value <= max;
  }

  const handleFeePercentageChange = (event) => {
    let value = event.target.value;
    if (value.length && (!isInteger(value) || !valueInRange(parseInt(value), 0, 100))) {
      return;
    }
    setChangesMade(true);
    setFeePercentage(value);
  };

  const handleSubscriptionTypeChange = (event) => {
    setChangesMade(true);
    setSubscriptionType(event.target.value);
  };

  const handleFromDateChange = (date) => {
    setChangesMade(true);
    if (date.isValid()) {
      setStartDate(moment(date).format(DateFormats.DAY));
    } else {
      setStartDate(null);
    }
  };

  const handleSubscriptionFeeChange = (event) => {
    let value = event.target.value;
    if (!validateCurrencyValue(value)) {
      return;
    }
    setChangesMade(true);
    setSubscriptionFee(parseCurrencyValue(value));
  };

  const handleServiceFeeChange = (event) => {
    let value = event.target.value;
    if (value.length && !valueInRange(parseFloat(value), 0, 100)) {
      return;
    }
    setChangesMade(true);
    setBillingFeePercentage(value);
  };

  const handleSubscriptionBillingFeeChange = (event) => {
    let value = event.target.value;
    if (value.length && !valueInRange(parseFloat(value), 0, 100)) {
      return;
    }
    setChangesMade(true);
    setSecurspaceServiceFeePercentage(event.target.value);
  };

  const handleResponseError = (error, message, number) => {
    snackbarShowMessage(message, 'error', number);
    Busy.set(false);
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    Busy.set(true);

    const body = {
      feePercentage: Number(feePercentage),
      billingFeePercentage: Number(billingFeePercentage),
      securspaceServiceFeePercentage: Number(securspaceServiceFeePercentage),
      subscriptionType: subscriptionType,
      subscriptionFee: subscriptionFee,
      subscriptionEffectiveDate: moment(startDate, DateFormats.DAY).isValid() ? moment(startDate, DateFormats.DAY).format(DateFormats.DAY) : startDate,
      activated: account.activated,
    }

    updateAccountAdmin(account.id, body).then(() => {
      requestLoggedInAuthoritySource().then((resp) => {
        handleAccountUpdated(resp.body);
        snackbarShowMessage('Admin info updated successfully', 'success', 8000);
      }).catch(error => {
        handleResponseError(error, 'An error occurred while updating the Admin info', 10000);
      }).finally(() => {
        Busy.set(false);
        setChangesMade(false);
      });
    }).catch(error => {
      handleResponseError(error, 'An error occurred while updating the Admin info', 15000);
    });
  };

  return <AdminInfoMarginTop20>
    <AdminInfoColumnGap5 item container component={'header'} alignContent={'center'}>
      <AdminPanelSettings color={'action'}/>
      <Typography variant={'h6'}>Admin Information</Typography>
    </AdminInfoColumnGap5>
    <AdminInfoRowGap10 container direction={'column'}>
      {isSupplierAccount(account) &&
        <>
          <TextField
            variant='standard'
            value={feePercentage}
            label={'Marketplace Commission Fee %'}
            name={'feePercentage'}
            onChange={handleFeePercentageChange}
            placeholder={'Enter the SecurSpace Fee %'}
            fullWidth
          />
          <FormControl variant="standard" className={"ss-byb-details-input"}>
            <InputLabel id='ssSubscriptionTypeSelect' variant={'standard'} color={'primary'}>Subscription
              Type</InputLabel>
            <Select
              value={subscriptionType || subscriptionTypeOptions[0]}
              label="Subscription Type"
              onChange={handleSubscriptionTypeChange}
              className='ss-byb-details-input'
              labelId="ssSubscriptionTypeSelect"
              fullWidth
            >
              {
                subscriptionTypeOptions?.map(item => (
                  <MenuItem key={item.value} value={item.value} name={item.label}>{item.label}</MenuItem>
                ))
              }
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <AdminInfoMarginTop5 container>
              <AdminInfoInputLabel
                id='ssSubscriptionStartDate'
                variant={'standard'}
                color={'primary'}
              >Subscription Start Date</AdminInfoInputLabel>
              <AdminInfoDatePicker
                value={moment(startDate, DateFormats.DAY)}
                onChange={handleFromDateChange}
                format={DateFormats.DAY}
                slotProps={{textField: {size: "small", error: false}}}
              />
            </AdminInfoMarginTop5>
          </LocalizationProvider>
          <TextField
            variant='standard'
            value={formatCurrencyValue(subscriptionFee)}
            label={'Subscription Fee'}
            name={'subscriptionFee'}
            onChange={handleSubscriptionFeeChange}
            placeholder={'Enter the Subscription Monthly Fee'}
            fullWidth
          />
          <TextField
            variant='standard'
            value={securspaceServiceFeePercentage}
            label={'Subscription Billing Fee %'}
            name={'securspaceServiceFeePercentage'}
            onChange={handleSubscriptionBillingFeeChange}
            placeholder={'Enter the Subscription Billing Fee %'}
            fullWidth
          />
          <TextField
            variant='standard'
            value={billingFeePercentage}
            label={'Service Fee %'}
            name={'serviceFee'}
            onChange={handleServiceFeeChange}
            placeholder={'Enter the Service Fee %'}
            fullWidth
          />
        </>
      }
    </AdminInfoRowGap10>
    <AdminInfoMarginButton
      type={'submit'}
      variant={'contained'}
      color={'secondary'}
      fullWidth
      disabled={!changesMade}
      onClick={handleSubmit}
    >
      Save Admin Information
    </AdminInfoMarginButton>
  </AdminInfoMarginTop20>
}

export default withSnackbar(AdminInfo);
